import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import Seo from "@/components/Seo";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

const FIELDS = [
  { k: "customer_name", label: "Full name", type: "text" },
  { k: "customer_email", label: "Email", type: "email" },
  { k: "customer_phone", label: "Phone", type: "tel" },
  { k: "address", label: "Address", type: "text", full: true },
  { k: "city", label: "City", type: "text" },
  { k: "state", label: "State", type: "text" },
  { k: "pincode", label: "Pincode", type: "text" },
];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const { cart, cartTotal, clearCart } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map((f) => [f.k, ""])));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handlePay = async () => {
    for (const f of FIELDS) {
      if (!form[f.k]) return toast.error(`Please enter ${f.label.toLowerCase()}`);
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { ...form, items: cart });
      const order = data.order;

      if (data.razorpay_available) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Payment SDK failed to load"); setLoading(false); return; }
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: Math.round(order.amount * 100),
          currency: "INR",
          name: "Indoforce",
          description: `Order ${order.id}`,
          order_id: order.razorpay_order_id,
          prefill: { name: form.customer_name, email: form.customer_email, contact: form.customer_phone },
          theme: { color: "#E31E24" },
          handler: async (res) => {
            try {
              await api.post("/orders/verify", {
                order_id: order.id,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              clearCart();
              setDone({ id: order.id, paid: true });
            } catch {
              toast.error("Payment verification failed");
            }
          },
          modal: { ondismiss: () => setLoading(false) },
        });
        rzp.open();
        setLoading(false);
      } else {
        // Razorpay not configured yet — record order as request/enquiry
        clearCart();
        setDone({ id: order.id, paid: false });
      }
    } catch (e) {
      toast.error("Could not place order");
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 pb-40 max-w-xl mx-auto px-6 text-center" data-testid="order-success">
        <Seo title="Order placed — Indoforce" description="Order confirmation" />
        <CheckCircle2 size={56} className="mx-auto text-[#E31E24] mb-6" />
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter">
          {done.paid ? "Payment successful" : "Order received"}
        </h1>
        <p className="text-white/60 mt-4">
          {done.paid
            ? "Thank you! Your payment is confirmed and your order is being processed."
            : "Thanks! Your order request has been recorded. Our team will contact you shortly to confirm payment & dispatch."}
        </p>
        <p className="text-white/40 mt-2 text-sm">Order ID: <span className="text-[#E31E24] font-mono">{done.id}</span></p>
        <button onClick={() => navigate("/shop")} className="btn-fill mt-8 inline-flex border border-[#E31E24] text-[#E31E24] hover:text-black px-8 py-4 uppercase text-sm font-bold tracking-widest">
          Continue shopping
        </button>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="text-white/60 mb-6">Your cart is empty.</p>
        <button onClick={() => navigate("/shop")} className="text-[#E31E24] uppercase text-sm tracking-widest">← Go to shop</button>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16 max-w-[1600px] mx-auto px-6 md:px-10">
      <Seo title="Checkout — Indoforce" description="Complete your order securely." />
      <h1 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none mb-12">Checkout</h1>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-6">Shipping details</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-2">{f.label}</label>
                <input
                  data-testid={`checkout-${f.k}`}
                  type={f.type}
                  value={form[f.k]}
                  onChange={(e) => set(f.k, e.target.value)}
                  className="w-full bg-transparent border border-white/20 focus:border-[#E31E24] outline-none px-4 py-3 text-sm"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="border border-white/10 p-6 h-fit lg:sticky lg:top-28">
          <h3 className="font-display text-xl font-bold uppercase tracking-tight mb-6">Your order</h3>
          <div className="space-y-3 mb-4 max-h-64 overflow-auto">
            {cart.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm text-white/70">
                <span className="pr-2">{i.name} × {i.qty}</span>
                <span>{formatINR(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-display font-black text-2xl border-t border-white/10 pt-4">
            <span>Total</span><span className="text-[#E31E24]">{formatINR(cartTotal)}</span>
          </div>
          <button
            onClick={handlePay}
            disabled={loading}
            data-testid="pay-btn"
            className="btn-fill w-full mt-6 flex items-center justify-center gap-3 border border-[#E31E24] text-[#E31E24] hover:text-black px-8 py-4 uppercase text-sm font-bold tracking-widest disabled:opacity-50"
          >
            {loading ? "Processing..." : "Pay with Razorpay"}
          </button>
          <p className="text-white/30 text-[11px] mt-3 text-center">Secure payments via Razorpay · UPI, Cards, Netbanking</p>
        </div>
      </div>
    </div>
  );
}
